import Header from '@src/shared/layouts/Dashboard/components/Header';
import React, { useEffect, useState } from 'react';
import useStyles from './styles';
import ManagerInitialContent from './components/ManagerInitialContent';
import CourierInitialContent from './components/CourierInitialContent';
import WelcomingContent from './components/WelcomingContent';
import TestimonialContent from './components/TestimonialContent';
import PageTypeSwitchContent from './components/PageTypeSwitchContent';
import HowItWorksContent from './components/HowItWorksContent';
import ManagerPromotionContent from './components/ManagerPromotionContent';
import CourierPromotionContent from './components/CourierPromotionContent';
import ManagerGetStartedContent from './components/ManagerGetStartedContent';
import CourierGetStartedContent from './components/CourierGetStartedContent';
import FAQContent from './components/FAQContent';
import BottomActionContent from './components/BottomActionContent';
import Footer from '@src/shared/components/Footer';
import i18n, { isLanguageActive, languageQueryParam } from '@src/i18n';
import { getBrowserLanguage } from '@src/shared/utils';
import useQueryParam from '@src/shared/hooks/use-queryparam';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import useOverviewAnalytics from './hooks/use-overview-analytics';
import { useHistory, useLocation } from 'react-router-dom';
import { useCurrentUserActions } from '@src/shared/context/current-user';
import { trackGetStarted } from './tracking/productoverview/page';
import utilStorage from '@src/shared/services/util-storage';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import OverviewScroller from './components/OverviewScroller';
import useGetFeedback from '@src/shared/hooks/use-getfeedback';

export enum PAGE_TYPE {
  MANAGER = 'manager',
  COURIER = 'courier',
}
export enum OS_TYPES {
  IOS = 'IOS',
  ANDROID = 'ANDROID',
}

export const APP_STORE_LINKS = {
  [OS_TYPES.ANDROID]: 'https://play.google.com/store/apps/details?id=com.takeaway.delivered4all',
  [OS_TYPES.IOS]: 'https://apps.apple.com/app/id1503301744',
};

const REDIRECTION_URLS = {
  [PAGE_TYPE.MANAGER]: '/product-overview',
  [PAGE_TYPE.COURIER]: '/courier-overview',
};

const NewOverviewPage = ({ predefinedPageType }: { predefinedPageType: PAGE_TYPE | undefined }) => {
  const classes = useStyles();
  const definedPageType = predefinedPageType || PAGE_TYPE.MANAGER;
  const [pageType, setPageType] = useState(definedPageType);
  const { params: queryParams } = useQueryParam(languageQueryParam);
  const { restaurant, keycloakToken } = useKeycloakUserState();
  const { setIsProductOverviewSeen } = useCurrentUserActions();
  const history = useHistory();
  const { isIOS, isAndroid } = useDeviceDetect();
  const { trackAPPDownload } = useOverviewAnalytics({ restaurant, pageType });
  const location = useLocation();
  const { triggerFeedbackCampaign } = useGetFeedback();

  useEffect(() => {
    if (queryParams && i18n.language !== queryParams) {
      if (isLanguageActive(queryParams)) {
        i18n.changeLanguage(queryParams);
      }
    } else if (!keycloakToken && !queryParams) {
      const browserLanguage = getBrowserLanguage();
      if (i18n.language !== browserLanguage) {
        i18n.changeLanguage(browserLanguage);
      }
    }
  }, [queryParams, keycloakToken]);

  useEffect(() => {
    if (location.pathname === REDIRECTION_URLS[PAGE_TYPE.MANAGER]) {
      setPageType(PAGE_TYPE.MANAGER);
    } else if (location.pathname === REDIRECTION_URLS[PAGE_TYPE.COURIER]) {
      setPageType(PAGE_TYPE.COURIER);
    }
  }, [location]);

  const pageTypeChangeHandler = (newPageType: PAGE_TYPE) => {
    history.push(REDIRECTION_URLS[newPageType]);
  };

  const handleCTAClick = async (buttonNumber: number) => {
    trackGetStarted(buttonNumber);
    await new Promise((resolve) => setTimeout(() => resolve(true), 300));
    utilStorage.setProductOverviewSeen();
    setIsProductOverviewSeen(true);
    history.push('/');
  };

  const androidAppDownloadHandler = () => {
    trackAPPDownload(OS_TYPES.ANDROID);
    window.open(APP_STORE_LINKS[OS_TYPES.ANDROID]);
  };

  const iOSAppDownloadHandler = () => {
    trackAPPDownload(OS_TYPES.IOS);
    window.open(APP_STORE_LINKS[OS_TYPES.IOS]);
  };

  const autoDetectDownloadHandler = () => {
    if (isIOS) {
      iOSAppDownloadHandler();
    } else if (isAndroid) {
      androidAppDownloadHandler();
    }
  };
  triggerFeedbackCampaign(
    'mcp_overview',
    ['de', 'fr', 'nl', 'pl', 'pt', 'bg', 'ro', 'dk', 'it', 'da'],
    restaurant?.country || navigator.language
  );

  return (
    <>
      <Header />
      <OverviewScroller />
      <div className={`${classes.section1} page_type_${pageType}`}>
        {pageType === PAGE_TYPE.MANAGER ? (
          <ManagerInitialContent onAction={() => handleCTAClick(1)} />
        ) : (
          <CourierInitialContent
            onAndroidDownload={androidAppDownloadHandler}
            onIosDownload={iOSAppDownloadHandler}
            onAutoDetectDownload={autoDetectDownloadHandler}
          />
        )}
      </div>
      <div className={classes.section2}>
        <WelcomingContent />
      </div>
      <div className={classes.section3}>
        <TestimonialContent pageType={pageType} />
      </div>
      <div className={classes.section4}>
        <PageTypeSwitchContent pageType={pageType} onPageTypeChanged={pageTypeChangeHandler} />
      </div>
      <div className={classes.section5} id="section-5">
        <HowItWorksContent pageType={pageType} />
      </div>
      <div className={classes.section6}>
        {pageType === PAGE_TYPE.MANAGER ? <ManagerPromotionContent /> : <CourierPromotionContent />}
      </div>
      <div className={classes.section7}>
        {pageType === PAGE_TYPE.MANAGER ? (
          <ManagerGetStartedContent onAction={() => handleCTAClick(2)} />
        ) : (
          <CourierGetStartedContent
            onAndroidDownload={androidAppDownloadHandler}
            onIosDownload={iOSAppDownloadHandler}
            onAutoDetectDownload={autoDetectDownloadHandler}
          />
        )}
      </div>
      <div className={classes.section8}>
        <FAQContent pageType={pageType} />
      </div>
      <div className={classes.section9}>
        <BottomActionContent
          onAction={() => handleCTAClick(3)}
          onAndroidDownload={androidAppDownloadHandler}
          onIosDownload={iOSAppDownloadHandler}
          onAutoDetectDownload={autoDetectDownloadHandler}
          pageType={pageType}
        />
      </div>
      <Footer />
    </>
  );
};

export default NewOverviewPage;
